import { parseClassroom } from "@examspark/api";
import { getTagFromResource } from "@examspark/common";
import {
  Classroom,
  Course,
  IDSK,
  isClassroom,
  isCourse,
  isUnit,
  Resource,
  SearchableResourceFilterInput,
  Unit,
} from "@examspark/types";

export type ResourceLocation =
  | Pick<Course, keyof IDSK | "resourcesIds" | "units" | "__typename">
  | Pick<Unit, keyof IDSK | "resourcesIds" | "__typename">
  | (Classroom & { resourceUnit?: Unit });

export type DecomposedResourceLocation = {
  course:
    | Pick<Course, keyof IDSK | "resourcesIds" | "units" | "__typename">
    | undefined;
  unit: Pick<Unit, keyof IDSK | "resourcesIds" | "__typename"> | undefined;
  classroom:
    | Pick<
        Classroom,
        | keyof IDSK
        | "units"
        | "__typename"
        | "schemaVersion"
        | "workspaceId"
        | "isArchived"
      >
    | undefined;
};

export type LocationType =
  | "Course"
  | "Unit"
  | "Classroom"
  | "ClassroomUnit"
  | undefined;

/** Given a course, unit, or classroom + resourceUnit combination, return the decomposed location */
export const decomposeResourceLocation = (
  location: ResourceLocation | undefined,
): DecomposedResourceLocation & { locationType: LocationType } => {
  let course: Course | undefined,
    unit: Unit | undefined,
    classroom: Classroom | undefined;

  let locationType: LocationType | undefined;

  if (isClassroom(location)) {
    course = location.course ?? undefined;
    locationType = "Classroom";
    if (location.resourceUnit != null) {
      unit = location.resourceUnit ?? undefined;
      locationType = "ClassroomUnit";
    }
    classroom = location;
  } else if (isUnit(location)) {
    course = location.course ?? undefined;
    unit = location;
    locationType = "Unit";
  } else if (isCourse(location)) {
    course = location;
    locationType = "Course";
  }

  return { course, unit, classroom: parseClassroom(classroom), locationType };
};

export const getResourceLocationFilter = (
  { course, unit, classroom }: DecomposedResourceLocation,
  courseUnits: Pick<Unit, keyof IDSK>[] | undefined,
) => {
  const filter: SearchableResourceFilterInput[] = [];

  if (course?.id != null) {
    filter.push({
      referencesIds: { eq: `course#${course.id}` },
    });
    if (unit?.id != null) {
      filter.push({ referencesIds: { eq: `unit#${unit.id}` } });
      filter.push({
        and: [
          { tags: { eq: `course-id#${course.id}` } },
          { tags: { eq: `unit-id#${unit.id}` } },
        ],
      });
      filter.push({
        and: [
          { tags: { eq: `course-id#${course.id}` } },
          { tags: { ne: "unit-id" } },
        ],
      });
    } else {
      filter.push(
        ...(courseUnits?.map((u) => ({
          referencesIds: { eq: `unit#${u.id}` },
        })) ?? []),
      );
      filter.push({ tags: { eq: `course-id#${course.id}` } });
    }
  }

  if (classroom?.id != null) {
    if (unit?.id != null) {
      filter.push({
        and: [
          { tags: { eq: `classroom-id#${classroom?.id}` } },
          { tags: { eq: `unit-id#${unit?.id}` } },
        ],
      });
      filter.push({
        and: [
          { tags: { eq: `classroom-id#${classroom?.id}` } },
          { tags: { ne: `unit-id` } },
        ],
      });
    } else {
      filter.push({ tags: { eq: `classroom-id#${classroom?.id}` } });
    }
  }

  if (filter.length === 0) return undefined;

  if (classroom?.id == null) {
    return [
      {
        and: [{ or: filter }, { tags: { ne: "classroom-id" } }],
      },
    ];
  }

  return filter;
};

export const determineResourceLocations = (
  item: Pick<Resource, keyof IDSK | "tags">,
  { course, classroom }: DecomposedResourceLocation,
  courseUnits: Pick<Unit, keyof IDSK | "resourcesIds">[] | undefined,
): ResourceLocation[] => {
  const locations: ResourceLocation[] = [];

  const taggedCourseId = getTagFromResource(item, "course-id")?.value;
  const taggedUnitId = getTagFromResource(item, "unit-id")?.value;
  const taggedClassroomId = getTagFromResource(item, "classroom-id")?.value;

  if (course?.resourcesIds?.find((r) => r.id === item.id)) {
    locations.push(course);
  } else if (
    course != null &&
    taggedCourseId === course?.id &&
    taggedUnitId == null &&
    taggedClassroomId == null
  ) {
    locations.push(course);
  }

  for (const courseUnit of courseUnits ?? []) {
    if (courseUnit.resourcesIds?.find((r) => r.id === item.id)) {
      locations.push(courseUnit);
    } else if (taggedUnitId === courseUnit.id && taggedClassroomId == null) {
      locations.push(courseUnit);
    }
  }

  if (classroom != null && taggedClassroomId === classroom?.id) {
    if (taggedUnitId == null) {
      locations.push({
        ...classroom,
        resourceUnit: undefined,
      });
    } else {
      const classroomUnit = classroom.units?.find(
        (u) => u.unit?.id === taggedUnitId,
      );
      if (classroomUnit != null) {
        locations.push({
          ...classroom,
          resourceUnit: classroomUnit.unit ?? undefined,
        });
      }
    }
  }

  return locations;
};
